import { autoUpdate, Placement, useFloating } from "@floating-ui/react-dom";
import { Popover, PopoverPanel, Transition } from "@headlessui/react";
import React, { ReactNode } from "react";
import tw from "src/utils/tw";
import { useMediaQueries } from "src/utils/useMediaQueries";

interface Props {
  children: ReactNode;
  content: ReactNode;
  keepOpen?: boolean;
  placement?: Placement;
  className?: string;
  disabled?: boolean;
  smallFull?: boolean;
  update?: boolean;
  menuClassName?: string;
  strategy?: "fixed" | "absolute";
}

function InputPopoverChildren(props: Props & { open: boolean }) {
  const { x, y, refs, strategy } = useFloating({
    placement: props.placement || "bottom-start",
    strategy: props.strategy
      ? props.strategy
      : props.smallFull
        ? "fixed"
        : "absolute",
    whileElementsMounted: props.update
      ? (ref, floating, update) =>
          autoUpdate(ref, floating, update, {
            animationFrame: true,
          })
      : undefined,
  });
  const { small } = useMediaQueries();

  let left = x ?? undefined;
  let right;

  if (props.smallFull && small) {
    left = 16;
    right = 16;
  }

  return (
    <span className="relative">
      <Popover.Button
        ref={refs.setReference}
        as={"span" as "button"}
        className={tw(
          "inline-block",
          "align-bottom",
          !props.disabled ? "cursor-pointer" : "",
          "transition",
          "overflow-hidden",
          "p-0",
          "text-left",
          "bg-transparent",
          "align-middle",
          "relative",
          props.className
        )}
        disabled={props.disabled}
      >
        {props.children}
      </Popover.Button>

      <div
        ref={refs.setFloating}
        className={tw(
          "z-30 w-80",
          props.smallFull && "sm:w-auto",
          props.menuClassName
        )}
        style={{
          position: strategy,
          top: y ?? undefined,
          left,
          right,
        }}
      >
        <Transition
          show={props.open}
          className={tw(
            "absolute mt-1 w-full origin-top transform overflow-hidden rounded bg-white shadow transition data-[closed]:scale-95 data-[closed]:opacity-0",
            props.smallFull && "sm:w-full"
          )}
          as="div"
          // unmount={false}
        >
          <PopoverPanel static>
            {({ close }) => (
              <div
                className="max-h-96 overflow-auto"
                onClick={() => {
                  if (!props.keepOpen) {
                    close();
                  }
                }}
              >
                {props.content}
              </div>
            )}
          </PopoverPanel>
        </Transition>
      </div>
    </span>
  );
}

export function InputPopover(props: Props) {
  return (
    <Popover as={React.Fragment}>
      {({ open }) => (
        <InputPopoverChildren open={open} {...props}>
          {props.children}
        </InputPopoverChildren>
      )}
    </Popover>
  );
}
